// src/pages/Clientes.tsx

import React, { useState, useEffect } from "react";
import { fetchClientes, Cliente } from "../api/clientesApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/concursos.css"; // Reutilizamos estilos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faCheck,
  faSpinner,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useSearchParams } from "react-router-dom";
import LoadingLottie from "../components/LoadingLottie";
import { getAuthToken } from "../security/AuthLogin";

const Clientes: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const [pendingSearchTerm, setPendingSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [pendingFechaRango, setPendingFechaRango] = useState<
    [Date | null, Date | null]
  >([
    searchParams.get("f_alta_inicio")
      ? new Date(searchParams.get("f_alta_inicio")!)
      : null,
    searchParams.get("f_alta_fin")
      ? new Date(searchParams.get("f_alta_fin")!)
      : null,
  ]);

  // Estado para el rango de fecha de baja
  const [pendingFechaRangoBaja, setPendingFechaRangoBaja] = useState<
    [Date | null, Date | null]
  >([
    searchParams.get("f_baja_inicio")
      ? new Date(searchParams.get("f_baja_inicio")!)
      : null,
    searchParams.get("f_baja_fin")
      ? new Date(searchParams.get("f_baja_fin")!)
      : null,
  ]);

  const [pendingOrdenarPor, setPendingOrdenarPor] = useState(
    searchParams.get("ordenarPor") || ""
  );

  const searchTerm = searchParams.get("search") || "";
  const fechaRango: [string, string] = [
    searchParams.get("f_alta_inicio") || "",
    searchParams.get("f_alta_fin") || "",
  ];
  // Strings para pasárselos a la API
  const fechaRangoBaja: [string, string] = [
    searchParams.get("f_baja_inicio") || "",
    searchParams.get("f_baja_fin") || "",
  ];

  const ordenarPor = searchParams.get("ordenarPor") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  // NUEVO: estado para spinner/tick en descarga
  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  const loadClientes = async () => {
    setLoading(true);
    setError(null);

    try {
      let currentPage = page;
      if (isNaN(currentPage) || currentPage < 1) currentPage = 1;

      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        f_alta_inicio: fechaRango[0],
        f_alta_fin: fechaRango[1],
        f_baja_inicio: fechaRangoBaja[0],
        f_baja_fin: fechaRangoBaja[1],
        ordenarPor,
      };

      const { data, total, limit } = await fetchClientes(params);
      setClientes(data);
      setTotalPages(Math.max(Math.ceil(total / limit), 1));
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error desconocido al cargar los clientes.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const applyFilters = () => {
    const params: Record<string, string> = {};

    if (pendingSearchTerm) params["search"] = pendingSearchTerm;
    if (pendingFechaRango[0]) {
      params["f_alta_inicio"] = new Date(
        pendingFechaRango[0].getTime() -
          pendingFechaRango[0].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingFechaRango[1]) {
      params["f_alta_fin"] = new Date(
        pendingFechaRango[1].getTime() -
          pendingFechaRango[1].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }

    // NUEVO: rango de fecha de baja
    if (pendingFechaRangoBaja[0]) {
      params["f_baja_inicio"] = new Date(
        pendingFechaRangoBaja[0].getTime() -
          pendingFechaRangoBaja[0].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingFechaRangoBaja[1]) {
      params["f_baja_fin"] = new Date(
        pendingFechaRangoBaja[1].getTime() -
          pendingFechaRangoBaja[1].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingOrdenarPor) params["ordenarPor"] = pendingOrdenarPor;

    params["page"] = "1";
    setSearchParams(params);
  };

  const applyOrdenarPor = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("ordenarPor", pendingOrdenarPor);
    newSearchParams.set("page", "1");
    setSearchParams(newSearchParams);
  };

  const handleFilterReset = () => {
    setPendingSearchTerm("");
    setPendingFechaRango([null, null]);
    setPendingFechaRangoBaja([null, null]);
    setPendingOrdenarPor("");
    setSearchParams({});
  };

  const changePage = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  // Descargar Excel => /api/clientes/export
  const downloadExcel = async () => {
    try {
      setExcelStatus("loading"); // Spinner ON

      const token = getAuthToken();
      if (!token) {
        alert("No hay token, inicia sesión");
        setExcelStatus("idle");
        return;
      }
      // No se usan filtros: se descarga TODO
      const exportUrl = `${
        `${window.location.protocol}//${window.location.hostname}` ||
        `${window.location.protocol}//${window.location.hostname}`
      }/api/clientes/export`;

      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error en descarga: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "clientes.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      // Exito => tick
      setExcelStatus("success");
      setTimeout(() => {
        setExcelStatus("idle");
      }, 2000);
    } catch (error) {
      console.error("Error al descargar Excel de clientes:", error);
      alert("Error al descargar Excel de clientes");
      setExcelStatus("idle");
    }
  };

  // Render para el botón con spinner/tick
  const renderExcelButtonContent = () => {
    if (excelStatus === "loading") {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando...
        </>
      );
    }
    if (excelStatus === "success") {
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    }
    // idle
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  return (
    <div className="container-concursos">
      <div className="ordenar-por">
        <label htmlFor="ordenarPor">Ordenar por:</label>
        <select
          id="ordenarPor"
          value={pendingOrdenarPor}
          onChange={(e) => setPendingOrdenarPor(e.target.value)}
          className="form-control"
        >
          <option value="">Seleccione...</option>
          <option value="nombre ASC">Nombre (A-Z)</option>
          <option value="nombre DESC">Nombre (Z-A)</option>
          <option value="email ASC">Email (A-Z)</option>
          <option value="email DESC">Email (Z-A)</option>
          <option value="f_alta ASC">Fecha Alta (antiguas primero)</option>
          <option value="f_alta DESC">Fecha Alta (recientes primero)</option>
        </select>
        <button onClick={applyOrdenarPor} className="btn btn-primary">
          Aplicar
        </button>
      </div>

      <div className="filtros">
        <input
          type="text"
          placeholder="Buscar por nombre, empresa o email..."
          value={pendingSearchTerm}
          onChange={(e) => setPendingSearchTerm(e.target.value)}
          className="form-control busqueda"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />

        <DatePicker
          selected={pendingFechaRango[0] || undefined}
          onChange={(dates: [Date | null, Date | null]) =>
            setPendingFechaRango(dates)
          }
          startDate={pendingFechaRango[0] || undefined}
          endDate={pendingFechaRango[1] || undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Rango de fechas de alta"
          className="form-control"
        />

        <DatePicker
          selected={pendingFechaRangoBaja[0] || undefined}
          onChange={(dates: [Date | null, Date | null]) =>
            setPendingFechaRangoBaja(dates)
          }
          startDate={pendingFechaRangoBaja[0] || undefined}
          endDate={pendingFechaRangoBaja[1] || undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Rango de fechas de baja"
          className="form-control"
        />

        <button
          onClick={applyFilters}
          className="btn btn-primary"
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
        >
          Filtrar
        </button>
        <button onClick={handleFilterReset} className="btn btn-secondary">
          Eliminar filtros
        </button>
      </div>

      <div style={{ display: "none" }}></div>

      {/* Botón para descargar Excel (todo el table) */}
      <div style={{ margin: "1rem 0" }}>
        <button
          onClick={downloadExcel}
          className="btn btn-success"
          disabled={excelStatus === "loading"}
        >
          {renderExcelButtonContent()}
        </button>
      </div>

      {loading ? (
        <LoadingLottie />
      ) : error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : clientes.length === 0 ? (
        <p>No hay clientes disponibles.</p>
      ) : (
        <div className="listing-container">
          {clientes.map((cliente) => (
            <div className="listing-item" key={cliente.id_usuario}>
              <div className="info">
                <div className="header-concurso">
                  <div className="nombre">
                    <strong>{`${cliente.nombre ?? ""}`.trim()}</strong>
                  </div>
                  <div className="subheader-concurso">
                    <div className="publication-date">
                      <span>Fecha alta:&nbsp;</span>
                      {cliente.f_alta
                        ? new Date(cliente.f_alta).toLocaleDateString()
                        : "No hay"}
                    </div>
                    <div className="deactivation-date">
                      <span>Fecha baja:&nbsp;</span>
                      {cliente.f_baja
                        ? new Date(cliente.f_baja).toLocaleDateString()
                        : "No hay"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="footer-clientes">
                <div className="actions-clientes">
                  <div className="email">{cliente.email ?? ""}</div>
                  <div className="empresa">
                    <strong>Empresa:</strong> {cliente.empresa || "N/A"}{" "}
                  </div>
                  <Link to={`/cliente/${cliente.id_usuario}`}>
                    <FontAwesomeIcon icon={faEye} title="Editar" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pagination-concursos">
        <button
          onClick={() => changePage(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Anterior
        </button>
        <span>
          Página {page} de {totalPages}
        </span>
        <button
          onClick={() => changePage(Math.min(page + 1, totalPages))}
          disabled={page === totalPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Clientes;
